<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import { getStore } from '@core/store';
import { useRouter } from 'vue-router';
import { createLevelAnimationSystem } from './scripts/LevelAnimationSystem.js';

const loading = ref(true);
const isAnimating = ref(false);
const levels = ref([]);

const router = useRouter();

const emit = defineEmits(['levelDataAvailable', 'itemClick']);

const store = getStore('rewards');
const userLevelData = ref({});

function showLevelDetail(level) {
  let levelToAnimate = { ...level };
  if (isAnimating.value) {
    levelToAnimate = { ...levels.value[userLevelData.value?.level - 1] };
  }
  const levelStepId = `#levelSetpOnMap-${levelToAnimate.level}`;
  const el = document.querySelector(levelStepId);
  el?.scrollIntoView({ block: 'end' });
  el?.click();
}

async function initLevelData() {
  const hasAnimated = await makeLevelAnimation();
  if (hasAnimated) return;

  refreshUserLevelData();
}

async function refreshUserLevelData() {
  if (!store.state.userLevelData) {
    return setTimeout(() => {
      refreshUserLevelData();
    }, 500);
  }
  userLevelData.value = store.state.userLevelData;
  const level = levels.value[(userLevelData?.value?.level || 1) - 1];

  await new Promise((r) => setTimeout(r, 2000));
  emit('levelDataAvailable', { ...userLevelData.value });
  loading.value = false;
}

async function makeLevelAnimation() {
  const store = getStore('rewards');
  const currentLevel = store.state.userLevelData;
  const previousUserLevelData = store.state.previousUserLevelData;
  const lastLevelAnimation = store.state.lastLevelAnimation;

  if (!currentLevel || !previousUserLevelData) return false;
  if (
    lastLevelAnimation &&
    lastLevelAnimation?.from == previousUserLevelData.level &&
    lastLevelAnimation &&
    lastLevelAnimation?.to == currentLevel.level
  )
    return false;

  emit('levelDataAvailable', { ...previousUserLevelData });
  await new Promise((r) => setTimeout(r, 1000));
  userLevelData.value = previousUserLevelData;
  isAnimating.value = true;
  loading.value = false;
  await new Promise((r) => setTimeout(r, 2000));
  userLevelData.value = currentLevel;
  await new Promise((r) => setTimeout(r, 500));
  showLevelDetail(levels.value[currentLevel.level - 1]);
  await new Promise((r) => setTimeout(r, 1000));

  await new Promise((r) => setTimeout(r, 600));
  emit('levelDataAvailable', { ...previousUserLevelData, xpPercent: 100 });
  await new Promise((r) => setTimeout(r, 600));
  emit('levelDataAvailable', { ...currentLevel, xpPercent: 0 });
  await new Promise((r) => setTimeout(r, 200));
  emit('levelDataAvailable', { ...currentLevel });
  return true;
}

function savedHasAnimated() {
  const store = getStore('rewards');
  const currentLevel = store.state.userLevelData;
  const previousUserLevelData = store.state.previousUserLevelData;

  store.state.lastLevelAnimation = {
    from: previousUserLevelData?.level,
    to: currentLevel.level
  };
  isAnimating.value = false;
}

async function makeWelcomeOnboarding() {
  const store = getStore('rewards');
  const currentLevel = store.state.userLevelData;
  if (!currentLevel) return false;

  isAnimating.value = true;
  userLevelData.value = currentLevel;
  await new Promise((r) => setTimeout(r, 500));
  showLevelDetail(levels.value[currentLevel.level - 1]);
  await new Promise((r) => setTimeout(r, 1000));
  emit('levelDataAvailable', { ...currentLevel });
  await new Promise((r) => setTimeout(r, 1000));
  isAnimating.value = false;
}

function goToHome() {
  router.push('/home');
}

onMounted(() => {
  bus.emit(constants.EVENTS.REWARDS.LOAD_LEVELS);

  const handleLevelsLoaded = (data) => {
    const rawData = Array.isArray(data)
      ? data
      : data?.data || data?.result || [];

    levels.value = rawData.map((level) => ({
      ...level,
      milestones: level.milestones || []
    }));
    initLevelData();
  };

  bus.on(constants.EVENTS.REWARDS.LEVELS_LOADED, handleLevelsLoaded);

  onUnmounted(() => {
    bus.off(constants.EVENTS.REWARDS.LEVELS_LOADED, handleLevelsLoaded);
  });
});

defineExpose({ makeWelcomeOnboarding });

// ==========================================
// ANIMATION DU REMPLISSAGE DES NIVEAUX
// ==========================================

const pathSegments = ref([]); // Référence directe aux éléments de segment
const basePathSegment = ref(null); // Référence pour l'ancre de base
const endPathSegment = ref(null); // Référence pour l'ancre de fin (top cap)

const LevelAnimationSystem = createLevelAnimationSystem(
  pathSegments,
  basePathSegment,
  endPathSegment
);

function smoothScrollToLevel(targetLevel, duration) {
  // Le container avec overflow-auto est dans RewardsPage.vue
  const container = document.querySelector('.overflow-auto');
  if (!container) return;

  const startPosition = container.scrollTop;

  // Calcul de la position cible basée sur 14rem par niveau
  const rem =
    parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const levelHeightPx = 12 * rem;

  // Dans flex-col-reverse, le niveau 1 est au plus bas (scrollTop = 0)
  // On décale d'une moitié de l'écran pour centrer le niveau ciblé
  let targetPosition =
    targetLevel * levelHeightPx -
    container.clientHeight / 2 +
    levelHeightPx / 2;
  targetPosition = Math.max(0, targetPosition); // On ne peut pas scroller en dessous de 0

  const distance = targetPosition - startPosition;
  let startTime = null;

  function animation(currentTime) {
    if (startTime === null) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const progress = Math.min(timeElapsed / duration, 1);

    // Ease-out quart (identique à LevelAnimationSystem)
    const easeOut = 1 - Math.pow(1 - progress, 4);

    const scroll = (startPosition + distance * easeOut) * -1;
    container.scrollTo({ top: scroll, behavior: 'instant' });

    if (timeElapsed < duration) {
      requestAnimationFrame(animation);
    }
  }

  requestAnimationFrame(animation);
}

const willPlayLevelAnimTimeout = ref(null);

watch(
  () => store.state.userLevelData,
  (newVal) => {
    refreshUserLevelData();
    clearTimeout(willPlayLevelAnimTimeout.value);
    willPlayLevelAnimTimeout.value = setTimeout(() => {
      if (newVal?.level && !store.state.loading) {
        const target =
          ((newVal.level || 1) - 1) * 100 +
          (newVal.currentLevelXpProgress || 0);
        LevelAnimationSystem.play(target);
        console.log('🚀 ~ target:', target);

        // Calcul de la durée de l'animation pour synchroniser le scroll
        const levelsToFill = Math.max(0, target / 100);
        const animationDuration = Math.min(500 + levelsToFill * 500, 4000);

        // Scroll synchronisé vers le niveau actif s'il est supérieur à 3
        if (newVal.level > 2) {
          setTimeout(() => {
            smoothScrollToLevel(newVal.level, animationDuration);
          }, LevelAnimationSystem.config.delayMs); // Commence exactement en même temps que l'animation
        }
      }
    }, 500);
  },
  { deep: true, immediate: true }
);

// ==========================================
</script>

<template>
  <div class="flex flex-col-reverse pointer-events-none px-6 noSaturate">
    <!-- Centered spinner when no data yet -->
    <div
      v-if="loading && levels.length === 0"
      class="fixed top-1/2 -translate-y-1/2 left-0 flex items-center justify-center w-full"
    >
      <div class="flex flex-col items-center gap-4">
        <span class="loading loading-spinner loading-lg text-primary"></span>
        <div class="text-sm opacity-70">Chargement des récompenses...</div>
      </div>
    </div>

    <!-- Base anchor path segment -->
    <div v-show="levels.length > 0">
      <div class="relative">
        <div
          class="border-white border-[2.6rem] h-[14rem] flex p-1 -mt-[2.6rem]"
          :class="{
            'border-t-0 ': true,
            'border-r-0': true
          }"
        ></div>
        <!-- First level base fill -->
        <div class="absolute top-0 left-0 w-full h-full pointer-events-none">
          <div
            ref="basePathSegment"
            class="border-primary border-[2.6rem] h-[14rem] flex p-1 -mt-[2.6rem]"
            :class="{
              'border-t-0 ': true,
              'border-r-0': true,
              'border-b-0': true
            }"
            style="display: none"
          ></div>
        </div>
      </div>
    </div>

    <!-- Dynamic path segments per level -->
    <div
      v-show="levels.length > 0"
      v-for="(level, levelIndex) in levels"
      :key="levelIndex"
      :id="'level-item-' + level.level"
    >
      <div class="relative">
        <!-- BASE PATH (White) -->
        <div class="absolute top-0 left-0 w-full pointer-events-none">
          <div
            class="border-[2.6rem] h-[14rem] p-1 border-white transition-colors duration-500"
            :class="{
              'border-b-0 !rounded-bl-none pb-[2.6rem]': levelIndex == 0,
              'rounded-l-[7rem] border-r-0 mr-[40%]': levelIndex % 2 == 0,
              'rounded-r-[7rem] border-l-0 ml-[40%] justify-end':
                levelIndex % 2 == 1
            }"
          ></div>
        </div>

        <!-- FILLED PATH (Primary) -->
        <div
          class="absolute top-0 pointer-events-none z-[1] w-2/3"
          :class="{
            'left-0': levelIndex % 2 == 0,
            'right-0': levelIndex % 2 == 1
          }"
        >
          <div
            ref="pathSegments"
            class="border-[2.6rem] h-[14rem] p-1 w-full border-primary"
            :class="{
              'border-b-0 !rounded-bl-none pb-[2.6rem]': levelIndex == 0,
              'rounded-l-[7rem] border-r-0 mr-[40%]d': levelIndex % 2 == 0,
              'rounded-r-[7rem] border-l-0 ml-[40%]d justify-end':
                levelIndex % 2 == 1
            }"
            style="display: none"
          ></div>
        </div>

        <!-- CONTENT -->
        <div
          class="border-[2.6rem] border-transparent h-[14rem] flex p-1 -mt-[2.6rem]"
          :class="{
            'border-b-0 !rounded-bl-none pb-[2.6rem]': levelIndex == 0,
            'rounded-l-[7rem] border-r-0 mr-[40%]': levelIndex % 2 == 0,
            'rounded-r-[7rem] border-l-0 ml-[40%] justify-end':
              levelIndex % 2 == 1
          }"
        >
          <div class="h-full w-[85vw] min-w-[85vw]">
            <div class="w-full h-full py-0 relative">
              <div
                class="flex gap-3 items-center h-full pointer-events-auto cursor-pointer transition-transform duration-300 hover:scale-[1.02]"
                @click="!loading && showLevelDetail(level)"
                :class="{
                  'pl-5 rounded-l-full': levelIndex % 2 == 0,
                  'flex-row-reverse text-right pr-5 rounded-r-full':
                    levelIndex % 2 == 1
                }"
              >
                <!-- Shimmer background on hover/base -->
                <div
                  class="absolute w-full h-full ripple top-0 left-0 z-[2] shimmerTarget"
                  :class="{
                    ' !rounded-bl-none': levelIndex == 0,
                    'pl-5 rounded-l-[4.4rem]': levelIndex % 2 == 0,
                    'flex-row-reverse text-right pr-5 rounded-r-[4.4rem]':
                      levelIndex % 2 == 1
                  }"
                  :style="{
                    maskImage: `linear-gradient(to ${levelIndex % 2 == 0 ? 'right' : 'left'}, white 0%, transparent 80%)`
                  }"
                ></div>

                <!-- Badge Image -->
                <div
                  class="p-2 h-full w-[25%] shimmerTarget shimmerTransparent z-[3]"
                >
                  <ImgComponent
                    src="/images/badge/badge2.png"
                    class="object-contain h-full w-full drop-shadow-xl "
                    :class="{
                      'opacity-20':
                        level.level <= userLevelData.level,
                      'opacity-100':
                        level.level == userLevelData.level + 1,
                      'opacity-60':
                        level.level > userLevelData.level + 1
                    }"
                  />
                </div>

                <!-- Level Texts -->
                <div class="col-span-2 z-[3]">
                  <div
                    class="title-color text-2xl font-bold shimmerTarget shimmerTransparent bg-gradient-to-r from-primary to-orange-500 bg-clip-text text-transparent"
                  >
                    Niveau {{ level.level }}
                  </div>
                  <div
                    class="mt-1 shimmerTarget shimmerTransparent text-gray-700 font-medium"
                  >
                    {{ level.name }}
                  </div>
                </div>
              </div>
            </div>

            <!-- LEVEL INDICATOR & TOOLTIP -->
            <div
              class="absolute py-[1.3rem] px-[1.4rem] w-full h-full top-0 left-0 z-[2]"
            >
              <div class="relative w-full h-full top-0 left-0 z-[2]">
                <div
                  class="pointer-events-auto w-[3rem] h-[3rem] absolute rounded-full -translate-y-1/2 flex items-center justify-center top-1/2"
                  :class="{
                    '-translate-x-1/2 left-0': levelIndex % 2 == 0,
                    'translate-x-1/2 right-0': levelIndex % 2 == 1
                  }"
                >
                  <div class="absolute">
                    <VDropdown
                      :distance="6"
                      theme="tooltip-dark"
                      placement="bottom"
                      no-auto-focus
                      :autoHide="true"
                      ref="tooltip"
                      :delay="{ show: 0, hide: 250 }"
                    >
                      <template #default="{ shown }">
                        <div
                          class="shimmerTarget shimmerTransparent"
                          :id="`levelSetpOnMap-${level.level}`"
                          style="scroll-margin-block-end: 12rem"
                        >
                          <div
                            @click.stop="showLevelDetail(level)"
                            class="ripple w-[4rem] h-[4rem] rounded-full flex items-center justify-center drop-shadow-lg ring-4"
                            :class="{
                              'bg-primary ring-primary-200/50':
                                level.level <= userLevelData.level,
                              'bg-primary ring-primary/50':
                                level.level == userLevelData.level + 1,
                              'bg-primary-400 ring-primary-400/50':
                                level.level > userLevelData.level + 1
                            }"
                          >
                            <span class="text-2xl text-white font-bold">
                              {{ level.level }}
                            </span>
                          </div>
                        </div>
                      </template>
                      <template #popper="{ hide }">
                        <div
                          class="p-4 relative !text-left w-[100vw] max-w-full"
                          :id="`tooltip-level-${level.level}`"
                        >
                          <div
                            class="absolute w-7 h-7 right-2 top-2 flex items-center justify-center border rounded-full text-base cursor-pointer hover:bg-white/20 transition-colors"
                            @click="hide"
                          >
                            &times;
                          </div>

                          <!-- TOOLTIP: ANIMATING STATE -->
                          <div v-if="isAnimating" class="min-w-[280px]">
                            <div class="flex items-center gap-3 mt-2">
                              <div
                                class="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-orange-400 flex items-center justify-center shadow-lg shadow-primary/30"
                              >
                                <span class="text-2xl">🎉</span>
                              </div>
                              <div>
                                <div
                                  class="text-sm font-medium text-primary-300 uppercase tracking-wider"
                                >
                                  Niveau {{ level.level }} atteint !
                                </div>
                                <div class="text-2xl font-black text-white">
                                  {{ level.name }}
                                </div>
                              </div>
                            </div>

                            <div
                              class="text-sm mt-4 text-gray-300 bg-white/5 p-3 rounded-xl border border-white/10"
                            >
                              {{ level.description }}
                            </div>

                            <div
                              v-if="level.rewards && level.rewards.length > 0"
                              class="mt-5"
                            >
                              <div class="flex items-center gap-2 mb-3">
                                <span class="text-xl">🎁</span>
                                <span class="font-bold text-lg text-white"
                                  >Récompenses débloquées</span
                                >
                              </div>
                              <div class="flex flex-col gap-2">
                                <div
                                  v-for="(reward, rewardIndex) in level.rewards"
                                  :key="rewardIndex"
                                  class="flex items-center gap-3 bg-gradient-to-r from-primary-500/20 to-transparent p-2.5 rounded-lg border border-primary-500/20"
                                >
                                  <div
                                    class="w-6 h-6 rounded-full bg-primary flex items-center justify-center shadow-md"
                                  >
                                    <span class="text-white text-xs">✓</span>
                                  </div>
                                  <span class="font-medium text-white">{{
                                    reward?.value || reward?.label
                                  }}</span>
                                </div>
                              </div>
                            </div>

                            <div
                              class="fixed rounded-full w-min bg-gradient-to-r from-primary-500/20 to-orange-500/20 z-10"
                              id="additionnalXps"
                              style="top: -20px; right: 20px"
                            >
                              <div
                                class="w-min whitespace-nowrap px-4 py-2 rounded-full flex gap-2 items-center text-sm text-primary-300 bg-primary-900/80 border border-primary-500/30 drop-shadow-xl shadow-primary/20"
                              >
                                <ColorImg
                                  src="/images/all/star.svg"
                                  class="object-contain h-5 w-5 bg-current"
                                />
                                <div class="font-bold">+200 XP</div>
                              </div>
                            </div>

                            <div class="mt-6 mb-2 flex justify-center">
                              <button
                                @click="
                                  () => {
                                    savedHasAnimated();
                                    if (level.level == 1) goToHome();
                                    hide();
                                  }
                                "
                                class="w-full button buttonComponent !text-lg !font-bold py-3 bg-gradient-to-r from-primary to-orange-500 hover:from-primary-400 hover:to-orange-400 transition-all duration-300 shadow-lg shadow-orange-500/20 rounded-xl"
                              >
                                {{
                                  level.level == 1
                                    ? 'Passer ma commande'
                                    : "Super, J'adore 😊"
                                }}
                              </button>
                            </div>
                          </div>

                          <!-- TOOLTIP: NORMAL STATE -->
                          <div v-else class="min-w-[280px]">
                            <!-- Header with status -->
                            <div class="flex items-center gap-4 mb-1">
                              <div
                                class="text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider"
                                :class="{
                                  'bg-green-500/20 text-green-400 border border-green-500/30':
                                    (userLevelData?.level || 1) >= level.level,
                                  'bg-primary-500/20 text-primary-300 border border-primary-500/30':
                                    (userLevelData?.level || 1) + 1 ===
                                    level.level,
                                  'bg-gray-500/20 text-gray-400 border border-gray-500/30':
                                    (userLevelData?.level || 1) + 1 <
                                    level.level
                                }"
                              >
                                <span
                                  v-if="
                                    (userLevelData?.level || 1) >= level.level
                                  "
                                  >✅ Terminé</span
                                >
                                <span
                                  v-else-if="
                                    (userLevelData?.level || 1) + 1 ===
                                    level.level
                                  "
                                  >🚀 En cours</span
                                >
                                <span v-else>🔒 Verrouillé</span>
                              </div>
                              <div class="text-sm font-bold text-gray-400">
                                Niv. {{ level.level }}
                              </div>
                            </div>

                            <div
                              class="text-3xl font-black mt-2 bg-gradient-to-r from-primary to-orange-400 bg-clip-text text-transparent"
                            >
                              {{ level.name }}
                            </div>

                            <div
                              class="text-sm mt-3 text-gray-300 leading-relaxed bg-white/5 p-3 rounded-xl border border-white/5"
                            >
                              {{ level.description }}
                            </div>

                            <!-- Rewards Section -->
                            <div
                              v-if="
                                level.rewards &&
                                level.rewards.length > 0 &&
                                level.level <= userLevelData.level
                              "
                              class="mt-5"
                            >
                              <div class="flex items-center gap-2 mb-3">
                                <span class="text-xl">🎁</span>
                                <span class="font-bold text-lg text-white"
                                  >Récompenses</span
                                >
                              </div>
                              <div class="flex flex-col gap-2">
                                <div
                                  v-for="(reward, rewardIndex) in level.rewards"
                                  :key="rewardIndex"
                                  class="flex items-center gap-3 p-2.5 rounded-lg border transition-colors"
                                  :class="
                                    (userLevelData?.level || 1) >= level.level
                                      ? 'bg-primary-500/10 border-primary-500/20'
                                      : 'bg-white/5 border-white/10'
                                  "
                                >
                                  <div
                                    class="w-6 h-6 rounded-full flex items-center justify-center shadow-sm"
                                    :class="
                                      (userLevelData?.level || 1) >= level.level
                                        ? 'bg-primary'
                                        : 'bg-gray-600'
                                    "
                                  >
                                    <span class="text-white text-xs">✓</span>
                                  </div>
                                  <span class="font-medium text-white">{{
                                    reward?.value || reward?.label
                                  }}</span>
                                </div>
                              </div>
                            </div>

                            <!-- XP Section (Only on the NEXT level to reach — where XP progress fills up) -->
                            <div
                              v-if="
                                (userLevelData?.level || 1) + 1 === level.level
                              "
                              class="mt-6 bg-black/20 p-4 rounded-xl border border-white/5"
                            >
                              <div class="flex justify-between items-end mb-2">
                                <div
                                  class="font-bold text-sm text-gray-400 uppercase tracking-wider"
                                >
                                  XP pour ce niveau
                                </div>
                                <div
                                  class="flex items-center gap-1.5 text-primary-300 font-black"
                                >
                                  <ColorImg
                                    src="/images/all/star.svg"
                                    class="w-4 h-4 bg-current"
                                  />
                                  <span>{{ level.requiredXp }} XP</span>
                                </div>
                              </div>
                              <div
                                class="flex justify-between text-xs font-bold text-white mb-1.5 mt-3"
                              >
                                <span>Progression</span>
                                <span
                                  >{{ userLevelData.totalXp ?? 0 }} /
                                  {{ level.requiredXp }}</span
                                >
                              </div>
                              <div
                                class="w-full h-2.5 bg-gray-800 rounded-full overflow-hidden border border-gray-700"
                              >
                                <div
                                  class="h-full bg-gradient-to-r from-primary to-orange-400 rounded-full relative"
                                  :style="{
                                    width: `${Math.min(100, userLevelData.currentLevelXpProgress || 0)}%`
                                  }"
                                >
                                  <div
                                    class="absolute inset-0 bg-white/20 animate-[shimmer_2s_infinite]"
                                  ></div>
                                </div>
                              </div>
                            </div>

                            <button
                              @click="
                                () => {
                                  goToHome();
                                }
                              "
                              v-if="
                                (userLevelData?.level || 1) + 1 === level.level
                              "
                              class="w-full button buttonComponent !text-lg !font-bold mt-5 py-3 bg-gradient-to-r from-primary to-orange-500 hover:from-primary-400 hover:to-orange-400 shadow-lg shadow-orange-500/20 rounded-xl transition-all"
                            >
                              {{ 'Faire une commande' }}
                            </button>
                          </div>
                        </div>
                      </template>
                    </VDropdown>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Top Cap (Last element visually at the top due to flex-col-reverse) -->
    <div v-show="levels.length > 0">
      <div class="relative">
        <div
          class="border-white border-[2.6rem] h-[14rem] flex p-1 -mt-[2.6rem]"
          :class="{
            'border-t-0 ': true,
            'rounded-l-[7rem] border-r-0 mr-[40%]': levels.length % 2 == 0,
            'rounded-r-[7rem] border-l-0 ml-[40%] justify-end':
              levels.length % 2 == 1
          }"
        ></div>
        <!-- Animated top cap fill -->
        <div
          class="absolute top-0 pointer-events-none z-[1] w-2/3"
          :class="{
            'left-0': levels.length % 2 == 0,
            'right-0': levels.length % 2 == 1
          }"
        >
          <div
            ref="endPathSegment"
            class="border-[2.6rem] h-[14rem] p-1 w-full border-primary"
            :class="{
              'border-t-0': true,
              'rounded-l-[7rem] border-r-0 mr-[40%]d': levels.length % 2 == 0,
              'rounded-r-[7rem] border-l-0 ml-[40%]d justify-end':
                levels.length % 2 == 1
            }"
            style="display: none"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>
